import React, { memo, useMemo, useCallback } from 'react';
import { createUseStyles, Icon } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { useWeb5Context } from 'lib/Web5Context';

import SectionList from 'components/SectionList';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ErrorList');

const warningIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
                d="M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
                fill=#edde83
                fillRule="evenodd"
            />
        </svg>`;

const errorIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2L1 21h22M12 6l7.53 13H4.47M11 10v4h2v-4m-2 6v2h2v-2" fill="#f04540" fill-rule="evenodd"></path></svg>`;

const ErrorList = ({ errors, setErrorMessage, warnings }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();

    const classNames = useMemo(
        () => (isDisabled, className) => {
            return classnames(
                {
                    [classes.iconDisabled]: isDisabled,
                },
                className,
            );
        },
        [classes.iconDisabled],
    );

    const errorList = useMemo(() => {
        let finalErrors = [];
        let finalWarnings = [];
        if (errors)
            finalErrors = errors.map((el, index) => ({
                ...el,
                error: t(el.error, [el.value || '']),
                id: `${index}-${el.row}`,
            }));
        if (warnings)
            finalWarnings = warnings.map((el, index) => ({
                ...el,
                isWarning: true,
                error: t(el.error, [el.value || '']),
                id: `${el.row}-${index}`,
            }));

        return [...finalWarnings, ...finalErrors];
    }, [errors, warnings, t]);

    const actions = useMemo(() => {
        return [
            (row) => {
                const isDisabled = !row?.error;
                const onClick = isDisabled ? null : () => setErrorMessage(true, row);
                return {
                    icon: (
                        <Icon
                            className={classNames(isDisabled, 'imports-row__viewError-btn')}
                            onClick={onClick}
                            name="visibilityOn"
                        />
                    ),
                    tooltip: { content: t('cfm_action_read_error_message') },
                };
            },
        ];
    }, [classNames, setErrorMessage, t]);

    const getAvatarSvg = useCallback((data) => (data.isWarning ? warningIcon : errorIcon), []);

    const listProps = useMemo(() => {
        return {
            className: classes.errorList,
            rows: errorList,
            actions: actions,
            columns: [
                {
                    field: 'row',
                    headerName: t('cfm_label_excel_row_number'),
                    resizable: false,
                    width: 200,
                },
                {
                    field: 'name',
                    headerName: t('cfm_label_column_name'),
                    resizable: false,
                },
                {
                    field: 'error',
                    headerName: t('cfm_label_description'),
                    resizable: false,
                    cellRenderer: 'avatarCell',
                    cellRendererParams: {
                        imageSize: 'small',
                        useTooltip: true,
                        isSquared: true,
                        innerMethods: {
                            getAvatarSvg: getAvatarSvg,
                        },
                    },
                },
            ],
            options: {
                suppressRowHoverHighlight: true,
                rowHeight: 40,
            },
        };
    }, [classes.errorList, errorList, actions, t, getAvatarSvg]);

    return <SectionList id="error-list" {...listProps} />;
};

export default memo(ErrorList);
